/**
 * `baseEdit` — the host-agnostic **mid-fight base-edit model**: the pure seam for the GM "equip a
 * weapon mid-fight" gesture. Always an encounter `edit_combatant_base` against the **live combatant
 * base** (combat-only — the source mech is untouched, and that divergence is the feature), **plus**
 * — only when "also persist to base mech" is ticked — a roster `equip_weapon` against the chosen
 * source mech.
 *
 * It runs the **real** `freezeWeaponProfile` (the same freeze the spawn adapters use, never
 * reinvented) and returns the command(s) to dispatch. **The pair is two commands, not a server
 * transaction** — the two rooms persist to separate files, so the host dispatches each to its own
 * room and surfaces an `{ ok:false }` from either inline.
 */
import type { Compendium } from "@lancer-vtt/compendium";
import type {
  Combatant,
  CombatantBase,
  EditCombatantBaseCommand,
  EquipWeaponCommand,
  PilotData,
  WeaponProfile,
} from "@lancer-vtt/domain";
import { freezeWeaponProfile, type RawWeapon } from "@lancer-vtt/rules";
import { type Result } from "@lancer-vtt/shared";

/** One weapon the picker offers — a thin id chooser sorted by display name. */
export type WeaponOption = { id: string; name: string };

/** The compendium's weapons as picker options, sorted by display name. Pure given the compendium. */
export function weaponOptions(compendium: Compendium): WeaponOption[] {
  return (compendium.list("weapon") ?? [])
    .map((w) => ({ id: w.id as string, name: (w.name as string) ?? (w.id as string) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * A roster mech the GM can write a weapon back into — a pilot/mech with at least one empty weapon
 * slot, resolved to the exact `(loadout, mount, slot)` the `equip_weapon` command targets. Keyed
 * `pilotId|mechId` for the picker.
 */
export type WriteBackTarget = {
  key: string;
  label: string;
  pilotId: string;
  mechId: string;
  loadoutId: string;
  mountIdx: number;
  slotIdx: number;
};

/**
 * The pilot/mech pairs the GM can persist a weapon back into — each with an active loadout and a
 * first empty weapon slot (the write-back target). A mech whose active loadout has no free slot is
 * skipped (nowhere to write). Pure: a stable mapping from the roster pilots to write-back targets.
 */
export function writeBackTargets(pilots: PilotData[]): WriteBackTarget[] {
  const out: WriteBackTarget[] = [];
  for (const p of pilots) {
    for (const m of p.mechs) {
      const loadout = m.loadouts[m.activeLoadoutIndex];
      if (!loadout) continue;
      // The first empty weapon slot — the write-back target.
      let mountIdx = -1;
      let slotIdx = -1;
      for (let mi = 0; mi < loadout.mounts.length && slotIdx < 0; mi++) {
        const mount = loadout.mounts[mi]!;
        for (let si = 0; si < mount.slots.length; si++) {
          if (!mount.slots[si]!.weaponId) {
            mountIdx = mi;
            slotIdx = si;
            break;
          }
        }
      }
      if (slotIdx < 0) continue; // no empty slot — can't write back here
      out.push({ key: `${p.id}|${m.id}`, label: `${p.callsign} · ${m.name}`, pilotId: p.id, mechId: m.id, loadoutId: loadout.id, mountIdx, slotIdx });
    }
  }
  return out;
}

export type BaseEditArgs = {
  actorId: string;
  combatant: Combatant;
  compendium: Compendium;
  weaponId: string;
  /** When present, also build the roster `equip_weapon` write-back (the "persist to base mech" half). */
  writeBack?: WriteBackTarget;
};

/** The cross-domain command pair: always the encounter edit, plus the roster equip when persisting. */
export type BaseEditCommands = {
  edit: EditCombatantBaseCommand;
  /** `null` unless a write-back target was given (combat-only edit). */
  equip: EquipWeaponCommand | null;
};

/**
 * Build the command(s) for equipping a weapon onto a combatant's live base. Looks the weapon up in
 * the compendium and freezes it (the real `freezeWeaponProfile`); a missing id is a clean
 * `{ ok:false }`. The new base appends the frozen profile to the combatant's weapons (replace
 * semantics — the reducer re-clamps `current` into the new bounds). When `writeBack` is given, also
 * builds the roster `equip_weapon` for the source mech; the host dispatches the two to their rooms.
 */
export function buildBaseEditCommands(args: BaseEditArgs): Result<BaseEditCommands> {
  const { actorId, combatant: c, compendium, weaponId, writeBack } = args;
  const raw = compendium.getById("weapon", weaponId) as RawWeapon | undefined;
  if (!raw) return { ok: false, error: `weapon "${weaponId}" not found in the compendium` };
  // A mid-fight append isn't mount/slot-indexed, so its instance id gets its own
  // `manual:${index}` namespace keyed on its append position in the combatant's existing
  // weapons — never collides with a spawn-time `${mount}:${slot}` or `integrated:`/`npc:` id.
  const instanceId = `manual:${(c.base.weapons ?? []).length}`;
  const profile: WeaponProfile = freezeWeaponProfile(raw, weaponId, instanceId);
  const base: CombatantBase = { ...c.base, weapons: [...(c.base.weapons ?? []), profile] };
  const edit: EditCombatantBaseCommand = { type: "edit_combatant_base", actorId, combatantId: c.id, base };
  const equip: EquipWeaponCommand | null = writeBack
    ? {
        type: "equip_weapon",
        actorId,
        pilotId: writeBack.pilotId,
        mechId: writeBack.mechId,
        loadoutId: writeBack.loadoutId,
        mountIdx: writeBack.mountIdx,
        slotIdx: writeBack.slotIdx,
        weaponId,
      }
    : null;
  return { ok: true, value: { edit, equip } };
}
